import { TableColumnDef, buildGridTemplate } from '../../utils/tableColumns';
import { LeadSortField } from './types';

export type FormFillColumnId =
    | 'formFill'
    | 'provider'
    | 'dateOfBirth'
    | 'caregiver'
    | 'location'
    | 'zip'
    | 'medCheck'
    | 'address'
    | 'sla'
    | 'communication'
    | 'status'
    | 'priority'
    | 'heroMember'
    | 'insuranceCarrier'
    | 'insurancePolicyId'
    | 'insuranceDiscovery'
    | 'createdAt'
    | 'completedAt'
    | 'updatedAt'
    | 'statusChangedAt'
    | 'expiresAt'
    | 'claimedBy'
    | 'exported'
    | 'latestNote'
    | 'nextAction';

export type FormFillColumnDef = TableColumnDef<FormFillColumnId, LeadSortField>;

/**
 * Single source of truth for the Form Fills list columns: header, grid track and
 * toggle state. Cell JSX in `LeadsTable` must follow this order — grid tracks are
 * positional. Defaults reproduce the Figma 5233:2772 layout, so every column added
 * on top of it ships hidden.
 */
export const FORM_FILL_COLUMNS: FormFillColumnDef[] = [
    { id: 'formFill', label: 'Form Fill', size: 'minmax(12rem,2fr)', alwaysVisible: true },
    { id: 'dateOfBirth', label: 'Date of Birth', size: '1.2fr', defaultVisible: false },
    { id: 'caregiver', label: 'Caregiver', size: '1.6fr', defaultVisible: false },
    { id: 'location', label: 'Location', size: '9rem', centered: true },
    { id: 'zip', label: 'ZIP', size: '1fr', defaultVisible: false },
    { id: 'medCheck', label: 'Med Check', size: '9rem', centered: true },
    { id: 'address', label: 'Address', size: '9rem', centered: true },
    { id: 'sla', label: 'SLA', size: '9rem', centered: true },
    { id: 'communication', label: 'Communication', size: '20rem', centered: true },
    { id: 'status', label: 'Status', size: '1fr', centered: true },
    { id: 'priority', label: 'Priority', size: '1fr', defaultVisible: false, centered: true },
    { id: 'heroMember', label: 'Hero Member', size: '1.2fr', defaultVisible: false },
    { id: 'insuranceCarrier', label: 'Insurance Carrier', size: '1.6fr', defaultVisible: false },
    { id: 'insurancePolicyId', label: 'Insurance ID', size: '1.4fr', defaultVisible: false },
    {
        id: 'insuranceDiscovery',
        label: 'Insurance Discovery',
        size: '1.6fr',
        defaultVisible: false
    },
    {
        id: 'createdAt',
        label: 'Created',
        size: '1.4fr',
        defaultVisible: false,
        sortField: 'created_at'
    },
    {
        id: 'completedAt',
        label: 'Completed',
        size: '1.4fr',
        defaultVisible: false,
        sortField: 'completed_at'
    },
    {
        id: 'updatedAt',
        label: 'Updated',
        size: '1.4fr',
        defaultVisible: false,
        sortField: 'updated_at'
    },
    { id: 'statusChangedAt', label: 'Status Changed', size: '1.4fr', defaultVisible: false },
    {
        id: 'expiresAt',
        label: 'Expires',
        size: '1.4fr',
        defaultVisible: false,
        sortField: 'expires_at'
    },
    { id: 'claimedBy', label: 'Claimed By', size: '1.6fr', defaultVisible: false },
    { id: 'exported', label: 'Exported', size: '1fr', defaultVisible: false },
    { id: 'latestNote', label: 'Sticky Note', size: '2fr', defaultVisible: false },
    { id: 'nextAction', label: 'Next Action', size: '2fr' }
];

/**
 * [HWM-23014] Provider attribution for cross-provider search results. Composed in per-login
 * rather than living in FORM_FILL_COLUMNS: a single-tenant user has nothing to attribute, so the
 * column must not appear in their layout OR in their column-control menu.
 */
export const PROVIDER_COLUMN: FormFillColumnDef = {
    id: 'provider',
    label: 'Provider',
    size: '1.6fr',
    // The documented exception to "every column added on top of Figma ships hidden": attributing a
    // cross-provider result is the point of the column, so a multi-org login gets it on by default.
    defaultVisible: true
};

/** Columns for this login: Provider sits next to the name it attributes, multi-org only. */
export const getFormFillColumns = (isMultiOrg: boolean): FormFillColumnDef[] => {
    if (!isMultiOrg) {
        return FORM_FILL_COLUMNS;
    }
    const afterFormFill = FORM_FILL_COLUMNS.findIndex((column) => column.id === 'formFill') + 1;
    return [
        ...FORM_FILL_COLUMNS.slice(0, afterFormFill),
        PROVIDER_COLUMN,
        ...FORM_FILL_COLUMNS.slice(afterFormFill)
    ];
};

/**
 * Base key; scope it with `useOrgStorageKey({ perUser: true })` so preferences are
 * held per user per organization.
 */
export const FORM_FILL_COLUMNS_STORAGE_KEY = 'ep:columns:form-fills';

/** Note cell — always shown (it is the only inline add/edit-note entry point). */
const NOTE_TRACK = '6rem';

export const getFormFillGridTemplate = (
    isVisible: (id: FormFillColumnId) => boolean,
    columns: FormFillColumnDef[] = FORM_FILL_COLUMNS
): string => buildGridTemplate(columns, isVisible, { trailingTracks: [NOTE_TRACK] });
